import { User } from "./User";
import "../global";
import { container } from "tsyringe";
import { ServiceTokens } from "../shared/ServiceTokens";
import { UserDataLayerAdapter } from "../shared/UserDataLayerAdapter";
import { UserDataLayerAdapterFactory } from "../shared/UserDataLayerAdapterFactory";
import { GtmEventNames } from "./GtmEventNames";

/**
 * Check if GTM is running in live mode, depending on the location of the page
 * and GTM variables.
 * */
export function appIsLive(productionHostNames: string[] | undefined): boolean {
  let isLive = false;
  if (productionHostNames?.includes(window.location.hostname.toLowerCase())) {
    const scriptSource = localStorage.getItem("DS_SCRIPT_SRC");
    if (scriptSource && scriptSource.toLowerCase() !== "prod") {
      return false;
    }

    const gtmEnvironment = dataLayerHelper.get<string>("gtmEnvironment");

    // If GTM's script is loaded without specifying an environment (without the gtm_auth and gtm_preview parameters),
    // the GTM variable "Environment Name" will return an empty string ("").
    // It's safer to assume that this "environment" is the production.
    // It's also Google's instruction to NOT use an environment-specific configuration in production.
    // See Install container snippets: https://support.google.com/tagmanager/answer/6311518?hl=en
    isLive = !gtmEnvironment || gtmEnvironment.toLowerCase() === "live";
  }
  return isLive;
}

/**
 * B2C pages doesn't host all the code that we need to implement all our requirements
 * and tracking it doesn't add any value.
 */
export function isPageB2C(): boolean {
  const hostName = window.location.hostname.toLowerCase();
  return hostName.indexOf("b2clogin.com") !== -1 || hostName.indexOf("login.cbc.radio-canada.ca") !== -1;
}

/**
 * Check if the user is in MyAccount or Login page
 */
export function isMyAccountOrLoginPage(): boolean {
  return window.location.hostname.startsWith("mon-compte.") || window.location.hostname.startsWith("login.cbc.");
}

export function getUserFromDataLayer(): User | undefined {
  return dataLayerHelper.get<User>("user");
}

/**
 * Push an event without payload to the data layer.
 * @param eventName The name of the event.
 */
export function pushEventToDataLayer(eventName: GtmEventNames): void {
  dataLayerQueue.push({
    event: eventName,
  });
}

export function getUserAdapter(): UserDataLayerAdapter | undefined {
  const adapterFactory = container.resolve<UserDataLayerAdapterFactory>(ServiceTokens.UserDataLayerAdapterFactory);
  const user = getUserFromDataLayer();
  if (user) {
    return adapterFactory.getUserDataAdapter(user);
  }
  return undefined;
}

/**
 * Return an instance of UserDataLayerAdapter wrapped around the current user
 * defined in the DataLayer or create a new adapter (with a new user) if the user isn't set.
 * @remarks
 * If a new user is created, it won't be pushed in the dataLayer.
 */
export function getOrCreateUserAdapter(): UserDataLayerAdapter {
  const userAdapter = getUserAdapter();
  if (userAdapter) {
    return userAdapter;
  }
  const adapterFactory = container.resolve<UserDataLayerAdapterFactory>(ServiceTokens.UserDataLayerAdapterFactory);
  return adapterFactory.getUserDataAdapter({});
}
